// @ts-nocheck
import {
  CFG,
  ROBES,
  SLOT_ORDER,
  SPELLS,
  SPELL_ORDER,
  START_LOADOUT,
  passiveFlatFromOwned,
  illusionDecoyCount,
} from "./data";
import {
  TAU,
  rand,
} from "./util";

/* ===================================================================
   ENTITIES
   =================================================================== */

export class Player {
  constructor(index, isHuman) {
    this.index = index;
    this.isHuman = isHuman;
    const r = ROBES[index % ROBES.length];
    this.robe = r.robe; this.trim = r.trim; this.colorName = r.name;
    this.radius = CFG.playerRadius;

    // persistent across rounds
    this.gold = CFG.startGold;
    this.skillPoints = CFG.startSkillPoints;
    this.passivePoints = CFG.startPassivePoints;
    this.synergyPoints = CFG.startSynergyPoints;
    this.passives = {};
    this.wins = 0;
    this.loadout = {}; this.committed = {};
    for (const slot in START_LOADOUT) {
      const id = START_LOADOUT[slot];
      this.loadout[slot] = id; this.committed[slot] = true;
    }
    this.items = {};
    // match stats
    this.dmgDealt = 0; this.kills = 0; this.dmgBySpell = {};
    this.dmgTaken = 0; this.healDone = 0; this.deaths = 0;
    this.dmgToOpp = {}; this.dmgFromSrc = {}; this.healBySrc = {};
    this.lastPlacement = 0;
    this.maxHP = 0;
    this.collR = 0; // server collision radius (bosses); 0 = wizard default
    this.collOffY = 0; // hit-center offset from entity origin
    this.name = isHuman ? "You" : this.colorName;
    this.title = "";
    this.titlePos = 0;
    this.admin = false;

    this.resetForRound();
  }

  // equipped spells in slot (keybind) order
  ownedSpells() { return SLOT_ORDER.map(s => this.loadout[s]).filter(Boolean); }
  equipped(id) { return Object.values(this.loadout).includes(id); }
  effMaxHP() { return (this.maxHP > 0 ? this.maxHP : CFG.maxHP + (this.maxHPBonus || 0)); }

  resetForRound() {
    recalcItems(this);
    this.x = 0; this.y = 0;
    this.vx = 0; this.vy = 0;
    this.aim = -Math.PI / 2;       // cursor direction (spell aim)
    this.facing = -Math.PI / 2;    // visual facing = movement direction
    this.alive = true;
    this.hp = this.effMaxHP();
    this.outside = false;
    this.falling = false;
    this.fallT = 0;          // 0..1 shrink progress while falling
    this.stun = 0;
    this.shieldT = 0;
    this.absorbHP = 0; this.absorbT = 0;
    this.reflectT = 0;
    this.hasteT = 0;
    // status effects
    this.root = 0; this.rootSpell = ""; this.silence = 0; this.freeze = 0; this.slowT = 0; this.slowMul = 1;
    this.invisT = 0; this.rushT = 0; this.burnT = 0; this.burnDPS = 0;
    this.hemorrhageT = 0;
    this.exhaustionT = 0;
    this.outsideT = 0;
    this.burnBy = null; this.burnSpell = "";
    // per-round combat stats
    this.dmgDealtRound = 0; this.dmgTakenRound = 0; this.healDoneRound = 0;
    this.killsRound = 0; this.deathsRound = 0;
    this.dmgBySpellRound = {}; this.dmgToOppRound = {}; this.dmgFromSrcRound = {}; this.healBySrcRound = {};
    this.tetherBy = null; this.tetherT = 0; this.tshiftT = 0; this.dashT = 0;
    this.dashSpell = "";
    this.channel = ""; this.channelT = 0; this.channelProg = 0;
    this.castPose = 0;
    this.moveTarget = null;
    this.spriteOct = null;
    this._spriteOctAcc = 0;
    this.cd = {}; SPELL_ORDER.forEach(id => this.cd[id] = 0);
    this._cdOverlayPeaks = {};
    this.lastHitBy = null;
    this.lastHitT = 0;
    this.knockT = 0;
    this.essenceMark = 0;
    this.attuneProg = 0;
    this.attuneToward = 0;
    // bot brain
    this.ai = { think: 0, jitterAngle: 0, repos: null };
    this.wobble = rand(0, TAU);
  }

  // Effective stats from owned passive nodes.
  dmgFlat(id) {
    return passiveFlatFromOwned(id, this.passives?.[id], "damage");
  }
  cdFor(id) {
    const s = SPELLS[id];
    if (!s?.cooldown) return 0;
    let cd = s.cooldown - passiveFlatFromOwned(id, this.passives?.[id], "cooldown");
    return Math.max(1, cd);
  }
  // Max CD for the current overlay sweep (handles exhaustion +3s and other bonuses above cdFor).
  cdOverlayMax(id) {
    const cd = this.cd?.[id] || 0;
    if (!this._cdOverlayPeaks) this._cdOverlayPeaks = {};
    if (cd <= 0) {
      delete this._cdOverlayPeaks[id];
      return Math.max(1, this.cdFor(id));
    }
    const base = Math.max(1, this.cdFor(id));
    this._cdOverlayPeaks[id] = Math.max(this._cdOverlayPeaks[id] || base, cd, base);
    return this._cdOverlayPeaks[id];
  }
}

// Aggregate owned items into derived combat stats (mirrors server recalcItems).
export function recalcItems(p) {
  p.moveMult = 1; p.mass = CFG.baseMass; p.lifesteal = 0; p.dmgBonus = 0;
  p.regen = 0; p.maxHPBonus = 0; p.rangeMult = 1; p.curseRes = 0; p.fireBurn = false;
  const it = p.items || {};
  for (const id in it) {
    const n = it[id]; if (!n) continue;
    if (id === "boots") { p.moveMult += 0.16 * n; p.mass -= 8 * n; }
    else if (id === "lavatreads") { p.moveMult += 0.15 * n; }
    else if (id === "pendant") { p.maxHPBonus += 60 * n; }
    else if (id === "cape") { p.regen += 3 * n; }
    else if (id === "helmet") { p.mass += 15 * n; p.rangeMult *= Math.pow(0.85, n); }
    else if (id === "bloodsword") { p.lifesteal += 0.25 * n; }
    else if (id === "soj") { p.dmgBonus += 0.20 * n; }
    else if (id === "pocketwatch") { p.curseRes = 0.4; }
    else if (id === "stafffireball") { p.fireBurn = true; }
  }
  if (p.mass < 35) p.mass = 35;
}

export class Projectile {
  constructor(owner, x, y, vx, vy, spellId, split) {
    this.owner = owner; this.spellId = spellId;
    this.x = x; this.y = y; this.vx = vx; this.vy = vy;
    const s = SPELLS[spellId];
    this.radius = s.radius; this.life = s.life; this.color = s.color;
    this.dead = false; this.t = 0;
    this.pierce = s.pierce || 0;
    this.bounce = s.bounce || 0;
    this.returning = false;
    this.startX = owner.x; this.startY = owner.y;
    this.hit = {};
    this.noSplit = !!split;
  }
}

export class Mine {
  constructor(owner, x, y) {
    this.owner = owner; this.x = x; this.y = y;
    this.arm = SPELLS.mine.armDelay; this.dead = false; this.pulse = 0;
  }
}

// Sigil Snare: a planted rune that arms after a delay, then roots/chills enemies
// who linger inside it until its lifetime runs out.
export class Sigil {
  constructor(owner, x, y, spellId) {
    const s = SPELLS[spellId];
    this.owner = owner; this.x = x; this.y = y; this.spellId = spellId;
    this.arm = s.armDelay; this.life = s.duration; this.r = s.radius;
    this.pulse = 0; this.dead = false;
  }
}

export class Particle {
  // opts (all optional): { kind, glow, drag, grav, spin }
  //   kind  : "dot" (default solid puff) | "glow" (additive soft blob) |
  //           "spark" (additive streak along velocity) | "smoke" (growing, fading)
  //   glow  : force additive blending even for a dot
  //   drag  : per-frame velocity multiplier (default 0.92)
  //   grav  : constant accel added to vy each second (smoke drifts up with a
  //           negative value, embers settle with a positive one)
  constructor(x, y, vx, vy, life, color, size, opts) {
    this.x = x; this.y = y; this.vx = vx; this.vy = vy;
    this.life = life; this.maxLife = life; this.color = color; this.size = size;
    const o = opts || EMPTY_OPTS;
    this.kind = o.kind || "dot";
    this.glow = o.glow || this.kind === "glow" || this.kind === "spark";
    this.drag = o.drag != null ? o.drag : 0.92;
    this.grav = o.grav || 0;
    this.spin = o.spin || 0;
  }
}
export const EMPTY_OPTS = {};

export class Pillar {
  constructor(x, y, r) {
    this.x = x; this.y = y; this.r = r;
    this.active = true;     // false once the void consumes it
    this.crumble = 0;       // 0..1 collapse animation
    this.seed = rand(0, TAU);
  }
}

// In-place array filter: keeps elements for which keep() is true, mutating the
// array rather than allocating a new one each frame (used on the per-frame
// particle/effect lists to avoid GC churn).
export function compact(arr, keep) {
  let w = 0;
  for (let i = 0; i < arr.length; i++) {
    if (keep(arr[i])) arr[w++] = arr[i];
  }
  arr.length = w;
}

// addNested books amt into a two-level map[src][spell] (mirrors server attribution).
export function addNested(m, src, spell, amt) {
  const key = spell || "";
  if (!m[src]) m[src] = {};
  m[src][key] = (m[src][key] || 0) + amt;
}
